import CustomInput from "@components/CustomInput/CustomInput";
import styles from "./PatientDetailComponent.module.css";
import DetailTableComponent from "@components/DetailTableComponent/DetailTableComponent";
import React, { useEffect, useState } from "react";
import Button from "@components/Button";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Home } from "@mui/icons-material";
import { useNavigate } from "react-router";
import useWindowSize from "@hooks/useWindowSize";
import usePatient from "@hooks/usePatient";
import usePopup from "@hooks/usePopup";
import ConfirmDetailsPopup from "@components/ConfirmDetailsPopup/ConfirmDetailsPopup";
import { notify } from "src/utils/notify";
import JsonDisplay from "@components/JsonDisplay/JsonDisplay"; // Import JsonDisplay component

const DoctorDetailComponent = ({ patientData }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [rows, setRows] = useState([]);
  const [patientsArray, setPatientsArray] = useState([]);
  const [editedPatientDetails, setEditedPatientDetails] = useState({});
  console.log("editedPatientDetails:", editedPatientDetails);
  const { width } = useWindowSize();
  const navigate = useNavigate();
  const { handleUpdatePatient } = usePatient();
  const { handlePopupCenterComponentRender, handlePopupCenterOpen } =
    usePopup();

  const fieldNameMappings = {
    "First Name": "firstName",
    "Last Name": "lastName",
    "Reference No": "referenceNo",
    Institute: "instituteName",
    Address: "address",
    Diagnosed: "Diagnosed",
    Pincode: "pincode",
    Province: "province",
    Country: "country",
    "2FAisEnabled": "isEnabled",
    "2FAexpiresAt": "2FAexpiresAt",
    "Request Type": "requestType",
    "Initial Level": "level",
    "Initial Time": "time",
    "Current Opacity 1": "opacity1",
    "Current Opacity 2": "opacity2",
    Verified: "isVerified",
    "Verification Date": "verifiedOn",
    Blocked: "isBlocked",
    "Blocked Date": "blockedOn",
    Reference: "referenceNumber",
    "Created By": "createdBy",
    Eye: "eye",
  };

  const getFieldName = (name) => fieldNameMappings[name] || name;

  const handleCopy = (copiedText) => {
    if (typeof copiedText === "object") {
      navigator.clipboard.writeText(JSON.stringify(copiedText?.props?.data));
      notify("Content Copied", "success");
    } else {
      navigator.clipboard.writeText(copiedText);
      notify("Content Copied", "success");
    }
  };

  function createData(name, code) {
    return {
      name,
      code,
      copy: (
        <ContentCopyIcon
          sx={{ cursor: "pointer" }}
          onClick={() => handleCopy(code)}
        />
      ),
    };
  }

  const extractPatientDetails = (patient) => {
    const obj = {
      "First Name": patient.local.firstName,
      "Last Name": patient.local.lastName,
      Email: patient.local.email, // Non-editable
      Contact: patient.local.contact, // Non-editable
      Eye: patient.local.eye,
      Diagnosed: <JsonDisplay data={patient.local.diagnosed} />,
      Address: patient.local.address,
      Pincode: patient.local.pincode,
      Province: patient.local.province,
      Country: patient.local.country,
      "2FAisEnabled": patient?.local?.["2FA"]?.isEnabled? "True" : "False",
      "2FAexpiresAt": patient?.local?.["2FA"]?.expiresAt,
      "Request Type": patient.requestType,
      "Created By": patient.createdBy,
      Reference: patient?.referenceNumber,
      "Latest Package": patient.latestPackage, // Non-editable
      "Latest Pay": patient.latestPay?.$numberDecimal, // Non-editable
      "Latest Subscription Start Date": patient.subscriptionStartDate, // Non-editable
      "Latest Subscribed Days": patient.subscriptionDays, // Non-editable
      "Package History": <JsonDisplay data={patient.packageHistory} />, // Render array of objects as JSON string
      "commentHistory": <JsonDisplay data={patient.commentHistory} />,
      "readingHistory": <JsonDisplay data={patient.readingHistory} />,
      "Non VR Exercise List": JSON.stringify(patient.exercises?.nonVR?.list), // Render object as JSON string
      "Non VR Initial Level": patient.exercises.nonVR.level,
      "Non VR Initial Time": patient.exercises.nonVR.time,
      "Non VR Current Opacity 1": patient.exercises.nonVR.opacity1.$numberDecimal,
      "Non VR Current Opacity 2": patient.exercises.nonVR.opacity2.$numberDecimal,
      "VR Exercise List": JSON.stringify(patient.exercises?.vr?.list), // Render object as JSON string
      "VR Initial Level": patient.exercises.vr.level,
      "VR Initial Time": patient.exercises.vr.time,
      "Non VR Exercise": <JsonDisplay data={patient.exercises.nonVR} />,
      "VR Exercise": <JsonDisplay data={patient.exercises.vr} />,
      Verified: patient.accountVerified.isVerified ? "True" : "False",
      "Verification Date": patient.accountVerified.verifiedOn,
      Blocked: patient.blockStatus.isBlocked ? "True" : "False",
      "Blocked Date": patient.blockStatus.blockedOn,
      "Created Date": patient.created_at,
      "Updated Date": patient.updated_at

    };
    const formattedArray = Object.entries(obj).map(([key, value]) => ({
      name: key,
      code: value,
    }));

    return formattedArray;
  };

  const handleInputChange = ({ target }) => {
    const { name, value } = target;
    setEditedPatientDetails((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };
  const isReactComponent = (obj) => React.isValidElement(obj);

  useEffect(() => {
    const updatedRows = isEditing
      ? patientsArray?.map(({ name, code }) => {
          // this is for non editable fields
          if (
            name === "Gender" ||
            name === "Email" ||
            name === "Reference No" ||
            name === "Contact" ||
            name === "Latest Package" ||
            name === "Latest Pay" ||
            name === "Latest Subscription Start Date" ||
            name === "Latest Subscribed Days" ||
            name === "Package History" ||
            name === "Created Date" ||
            name === "Updated Date" ||
            name === "Diagnosed" ||
            name === "2FAisEnabled" ||
            name === "2FAexpiresAt" ||
            name === "commentHistory" ||
            name === "readingHistory" ||
            name === "Non VR Exercise List" ||
            name === "Non VR Initial Level" ||
            name === "Non VR Initial Time" ||
            name === "Non VR Current Opacity 1" ||
            name === "Non VR Current Opacity 2" ||
            name === "VR Exercise List" ||
            name === "VR Initial Level	" ||
            name === "VR Initial Time	" ||
            name === "Non VR Exercise" ||
            name === "VR Exercise" 
          ) {
            return createData(name, code);
          } else {
            return createData(
              name,
              <CustomInput
                value={
                  editedPatientDetails[getFieldName(name)] !== undefined
                    ? editedPatientDetails[getFieldName(name)]
                    : isReactComponent(code)
                      ? code.props.data
                      : code
                }
                name={getFieldName(name)}
                onChange={handleInputChange}
              />
            );
          }
        })
      : patientsArray?.map(({ name, code }) => {
          return createData(name, code);
        });
    setRows(updatedRows);
  }, [isEditing, patientsArray, editedPatientDetails]);

  useEffect(() => {
    if (patientData) {
      const formattedArray = extractPatientDetails(patientData);
      setPatientsArray(formattedArray);
    }
  }, [patientData]);

  const columns = [
    { id: "name", label: "Field Name", minWidth: 170 },
    { id: "code", label: "Field Value", minWidth: 170 },
    {
      id: "copy",
      label: "Copy",
      minWidth: 85,
      align: "left",
      format: (value) => value.toFixed(2),
    },
  ];

  const handleEdit = () => {
    setIsEditing(true);
  };

  const onSuccess = () => {
    setEditedPatientDetails({});
  };

  const handleOnConfirm = () => {
    handleUpdatePatient(
      patientData?._id,
      {
        local: {
          ...patientData?.local,
          ...editedPatientDetails,
          requestType: undefined,
          level: undefined,
          time: undefined,
          opacity1: undefined,
          opacity2: undefined,
          isVerified: undefined,
          verifiedOn: undefined,
          isBlocked: undefined,
          blockedOn: undefined,
          referenceNumber: undefined,
          createdBy: undefined,
        },
        createdBy: editedPatientDetails?.createdBy ?? patientData?.createdBy,
        referenceNumber:
          editedPatientDetails?.referenceNumber ?? patientData?.referenceNumber,
        accountVerified: {
          ...patientData?.accountVerified,
          isVerified:
            editedPatientDetails?.isVerified ??
            patientData?.accountVerified?.isVerified,
          verifiedOn:
            editedPatientDetails?.verifiedOn ??
            patientData?.accountVerified?.verifiedOn,
        },
        blockStatus: {
          ...patientData?.blockStatus,
          isBlocked:
            editedPatientDetails?.isBlocked ??
            patientData?.blockStatus?.isBlocked,
          blockedOn:
            editedPatientDetails?.blockedOn ??
            patientData?.blockStatus?.blockedOn,
        },
        proofUri: patientData?.proofUri,
        requestType: editedPatientDetails?.requestType
          ? editedPatientDetails?.requestType
          : patientData?.requestType,
        myReferenceNumber: patientData?.myReferenceNumber,
        exercises: {
          ...patientData?.exercises,
        },
      },
      onSuccess
    );
    handlePopupCenterOpen(false);
  };

  const handleCancelConfirm = () => {
    handlePopupCenterOpen(false);
    setEditedPatientDetails({});
  };

  const handleSave = () => {
    setIsEditing(false);
    handlePopupCenterOpen(true);
    handlePopupCenterComponentRender(
      <ConfirmDetailsPopup
        details={editedPatientDetails}
        onConfirm={handleOnConfirm}
        onClose={handleCancelConfirm}
      />
    );
  };

  const handleNavigateDashboard = () => {
    navigate("/");
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setEditedPatientDetails({});
  };

  const handleNavigatePatient = () => {
    navigate("/managepatient");
  };

  const convertArrayToCSV = (data) => {
    const csvRows = [];
    // Get the headers
    const headers = Object.keys(data[0]);
    csvRows.push(headers.join(","));

    // Loop over the rows
    for (const row of data) {
      const values = headers.map((header) => {
        const value = row[header];
        let escaped;
        if (typeof value === "object" && value !== null) {
          escaped = JSON.stringify(value?.props?.data);
        } else {
          escaped = ("" + value).replace(/"/g, '\\"');
        }
        return `"${escaped}"`;
      });
      csvRows.push(values.join(","));
    }

    return csvRows.join("\n");
  };

  const downloadCSV = () => {
    const csvData = convertArrayToCSV(patientsArray);
    const blob = new Blob([csvData], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.setAttribute("hidden", "");
    a.setAttribute("href", url);
    a.setAttribute("download", `patient:${patientData?.local?.email}.csv`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className={styles.Wrapper}>
      <div className={styles.BreadCrumb}>
        <div className={styles.NavigationWrapper}>
          <Home className={styles.HomeIcon} onClick={handleNavigateDashboard} />{" "}
          <div>
            <span
              className={styles.DashboardText}
              onClick={handleNavigateDashboard}
            >
              Dashboard
            </span>
            <span
              className={`${styles.RouteText} ${styles.CursorPointer}`}
              onClick={handleNavigatePatient}
            >
              {" "}
              /Patient
            </span>
            <span className={styles.RouteText}> /{patientData?._id}</span>
          </div>
        </div>
        <div className={styles.BtnWrapper}>
          {width > 600 ? (
            <Button
              text={"Export to CSV"}
              btnType={"secondary"}
              onClick={downloadCSV}
            />
          ) : null}
          {isEditing ? (
            <>
              <Button btnType={"primary"} text={"Save"} onClick={handleSave} />
              <Button
                btnType={"secondary"}
                text={"Cancel"}
                onClick={handleEditCancel}
              />
            </>
          ) : (
            <Button btnType={"secondary"} text={"Edit"} onClick={handleEdit} />
          )}
        </div>
      </div>
      <DetailTableComponent rows={rows} columns={columns} />
      {width > 600 ? null : (
        <Button
          text={"Export to CSV"}
          btnType={"secondary"}
          onClick={downloadCSV}
          className={styles.ExportCsvBtn}
        />
      )}
    </div>
  );
};

export default DoctorDetailComponent;
